import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { AlertTriangle } from 'lucide-react';

const leaveTypes = ['Annual Leave', 'Sick Leave', 'Family Responsibility Leave', 'Unpaid Leave', 'Public Holiday Leave', 'Other'];

export function LeaveModals({
  showNewRequestModal, setShowNewRequestModal, newRequest, setNewRequest, employees, handleCreateRequest,
  discPrompt, setDiscPrompt, caseForm, setCaseForm, creatingCase, handleCreateDisciplinaryCase,
  unpaidConfirm, setUnpaidConfirm, confirmingUnpaid, handleConfirmUnpaid,
}) {
  return (
    <>
            {/* New Request Modal */}
            <Dialog open={showNewRequestModal} onOpenChange={setShowNewRequestModal}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>New Leave Request</DialogTitle>
                  <DialogDescription>Create a leave request for an employee</DialogDescription>
                </DialogHeader>
          
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Employee *</Label>
                    <Select 
                      value={newRequest.employee_id} 
                      onValueChange={(v) => setNewRequest({...newRequest, employee_id: v})}
                    >
                      <SelectTrigger data-testid="select-leave-employee">
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.employee_id} value={emp.employee_id}>
                            {emp.full_name} - {emp.department}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Leave Type *</Label>
                    <Select 
                      value={newRequest.leave_type} 
                      onValueChange={(v) => setNewRequest({...newRequest, leave_type: v})}
                    >
                      <SelectTrigger data-testid="select-leave-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {leaveTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date *</Label>
                      <Input
                        type="date"
                        value={newRequest.start_date}
                        onChange={(e) => setNewRequest({...newRequest, start_date: e.target.value})}
                        data-testid="input-leave-start"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date *</Label>
                      <Input
                        type="date"
                        value={newRequest.end_date}
                        onChange={(e) => setNewRequest({...newRequest, end_date: e.target.value})}
                        data-testid="input-leave-end"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Reason</Label>
                    <Textarea
                      value={newRequest.reason}
                      onChange={(e) => setNewRequest({...newRequest, reason: e.target.value})}
                      placeholder="Optional: Provide reason for leave..."
                      rows={3}
                      data-testid="input-leave-reason"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNewRequestModal(false)}>Cancel</Button>
                  <Button onClick={handleCreateRequest} data-testid="create-leave-btn">Create Request</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Disciplinary Prompt (appears after approving an unpaid leave that
                takes the employee below zero days) */}
            <Dialog open={!!discPrompt} onOpenChange={(open) => !open && setDiscPrompt(null)}>
              <DialogContent className="sm:max-w-lg" data-testid="disciplinary-prompt-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                    Open Disciplinary Case?
                  </DialogTitle>
                  <DialogDescription>{discPrompt?.message}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Case Type *</Label>
                    <Select
                      value={caseForm.case_type}
                      onValueChange={(v) => setCaseForm((f) => ({ ...f, case_type: v }))}
                    >
                      <SelectTrigger data-testid="disc-case-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Verbal Warning">Verbal Warning</SelectItem>
                        <SelectItem value="Written Warning">Written Warning</SelectItem>
                        <SelectItem value="Final Written Warning">Final Written Warning</SelectItem>
                        <SelectItem value="Suspension">Suspension</SelectItem>
                        <SelectItem value="Hearing">Hearing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description / Notes *</Label>
                    <Textarea
                      rows={4}
                      value={caseForm.description}
                      onChange={(e) => setCaseForm((f) => ({ ...f, description: e.target.value }))}
                      data-testid="disc-case-description"
                    />
                  </div>
                </div>

                <DialogFooter className="flex flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDiscPrompt(null)}
                    disabled={creatingCase}
                    data-testid="disc-skip-btn"
                  >
                    Skip
                  </Button>
                  <Button
                    onClick={handleCreateDisciplinaryCase}
                    disabled={creatingCase}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="disc-create-btn"
                  >
                    {creatingCase ? 'Creating…' : 'Open Case'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Unpaid Leave confirmation — no hard block; admin confirms when the
                balance would drop below -3 days so behaviour can be monitored. */}
            <Dialog open={!!unpaidConfirm} onOpenChange={(open) => !open && setUnpaidConfirm(null)}>
              <DialogContent className="sm:max-w-md" data-testid="unpaid-confirm-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                    Confirm Unpaid Leave
                  </DialogTitle>
                  <DialogDescription data-testid="unpaid-confirm-warning">
                    {unpaidConfirm?.warning}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="flex flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setUnpaidConfirm(null)}
                    disabled={confirmingUnpaid}
                    data-testid="unpaid-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmUnpaid}
                    disabled={confirmingUnpaid}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="unpaid-confirm-btn"
                  >
                    {confirmingUnpaid ? 'Approving…' : 'Confirm & Proceed'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
    </>
  );
}
